import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { CreditCard, Banknote, Smartphone, Loader2, LogOut, AlertTriangle, PercentSquare, BellRing, Receipt } from 'lucide-react';

/**
 * ShiftHandoverModal
 * ------------------
 * End-of-shift summary for the Till Supervisor. Opens when the user
 * clicks Logout (or the Clock Out button). Fetches `/till/shift-summary`
 * which aggregates everything they touched since their clock-in:
 *
 *   • Total orders settled + $
 *   • Payment-method split (EFTPOS / Cash / Apple Pay / Tab) — drives the
 *     cash-drawer reconciliation conversation with the incoming shift
 *   • Discounts approved + value, rejected count
 *   • Service bells resolved
 *   • Pending tabs the next shift inherits — biggest source of handover
 *     confusion, so we surface them prominently
 *
 * Two-step UX: review numbers → confirm logout. Cancel keeps them on
 * the panel (e.g. they spot a pending tab and want to settle it first).
 */
export default function ShiftHandoverModal({ open, onClose, onConfirmLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setData(null); return; }
    setLoading(true);
    api.get('/till/shift-summary')
      .then(res => setData(res.data))
      .catch(err => toast.error(err.response?.data?.detail || 'Could not load shift summary'))
      .finally(() => setLoading(false));
  }, [open]);

  const totals = data?.orders_settled || { count: 0, total: 0 };
  const m = data?.by_method || {};
  const d = data?.discounts || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-bg-secondary border border-gold/30 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shift-handover-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-white text-xl flex items-center gap-2">
            <Receipt className="w-5 h-5 text-gold" /> End of shift handover
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gold" />
          </div>
        ) : !data ? null : (
          <div className="space-y-4 pt-2 font-sans">
            {/* Who + window */}
            <div className="text-xs text-zinc-500">
              <span className="text-white">{data.user.name}</span> · shift since {fmtTime(data.shift_start)}
            </div>

            {/* Headline tile */}
            <div className="bg-gold/[0.05] border border-gold/30 rounded-sm p-4" data-testid="shift-headline">
              <p className="text-[10px] uppercase tracking-[0.18em] text-gold/70">Orders settled</p>
              <p className="text-4xl font-serif text-gold font-semibold mt-1">${totals.total.toFixed(2)}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{totals.count} order{totals.count === 1 ? '' : 's'}</p>
            </div>

            {/* Payment-method split — the cash-up table */}
            <div data-testid="shift-by-method">
              <h4 className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Payment method breakdown</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MethodTile icon={CreditCard} label="EFTPOS"     value={m.eftpos}     accent="text-blue-accent" testId="shift-method-eftpos" />
                <MethodTile icon={Banknote}  label="Cash"        value={m.cash}       accent="text-emerald-300" testId="shift-method-cash" />
                <MethodTile icon={Smartphone} label="Apple Pay"  value={m.apple_pay}  accent="text-zinc-200"     testId="shift-method-apple_pay" />
                <MethodTile icon={Receipt}   label="Tab settled" value={m.tab}        accent="text-amber-300"    testId="shift-method-tab" />
              </div>
            </div>

            {/* Discounts + service bells row */}
            <div className="grid grid-cols-2 gap-2" data-testid="shift-discount-bell">
              <div className="bg-white/[0.03] border border-white/10 rounded-sm p-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                  <PercentSquare className="w-3 h-3" /> Discounts
                </div>
                <p className="text-white text-sm">
                  Approved <span className="text-emerald-300 font-mono">{d.approved_count}</span>
                  <span className="text-zinc-500"> · ${(d.approved_value || 0).toFixed(2)}</span>
                </p>
                <p className="text-zinc-400 text-xs mt-0.5">Rejected <span className="text-red-300 font-mono">{d.rejected_count}</span></p>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded-sm p-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                  <BellRing className="w-3 h-3" /> Service bells
                </div>
                <p className="text-white text-2xl font-serif">{data.service_bells_resolved}</p>
                <p className="text-zinc-500 text-[10px]">resolved this shift</p>
              </div>
            </div>

            {/* Pending tabs — main handover risk */}
            <div data-testid="shift-pending-tabs">
              <h4 className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2 flex items-center gap-1.5">
                {data.pending_tabs.length > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                Pending tabs inherited by next shift ({data.pending_tabs.length})
              </h4>
              {data.pending_tabs.length === 0 ? (
                <p className="text-emerald-400 text-sm bg-emerald-500/[0.05] border border-emerald-500/20 rounded-sm px-3 py-2">
                  Clean handover — every tab is settled.
                </p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {data.pending_tabs.map(t => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between gap-2 py-2 px-3 bg-amber-500/[0.04] border border-amber-500/20 rounded-sm text-sm"
                      data-testid={`pending-tab-${t.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-gold font-serif font-semibold">T{t.table_number || '?'}</span>
                        <span className="text-white truncate">{t.customer_name}</span>
                      </div>
                      <Badge className="bg-amber-500/15 text-amber-300 border-0 font-mono text-[11px]">
                        ${t.current_total.toFixed(2)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button
                onClick={onClose}
                className="h-11 bg-transparent border border-white/15 text-zinc-300 hover:bg-white/[0.05] rounded-sm uppercase tracking-[0.12em] text-xs"
                data-testid="shift-stay-btn"
              >
                Stay on shift
              </Button>
              <Button
                onClick={onConfirmLogout}
                className="h-11 bg-gold text-black font-bold hover:bg-gold-light rounded-sm uppercase tracking-[0.12em] text-xs"
                data-testid="shift-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Confirm logout
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function MethodTile({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-sm p-2.5" data-testid={testId}>
      <div className={`flex items-center gap-1.5 ${accent} text-[10px] uppercase tracking-[0.15em] font-semibold`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`text-xl font-serif mt-1 ${accent}`}>${(value || 0).toFixed(2)}</p>
    </div>
  );
}
